import { useState, useRef, useEffect, useLayoutEffect, useCallback, cloneElement, forwardRef, isValidElement, type ReactElement, type ReactNode, type Ref } from 'react'
import { createPortal } from 'react-dom'

export type TooltipPlacement = 'right' | 'top' | 'bottom'

let tooltipCounter = 0

interface TooltipProps {
  /** Tooltip content. A plain string stays single-line; pass nodes for rich layout. */
  text: ReactNode
  placement?: TooltipPlacement
  delayMs?: number
  /**
   * Override the box's max-width (the `.tm-tooltip` default is 300px). Pass
   * `'none'` to let the box auto-size to its content (e.g. a one-line commit hash
   * that shouldn't wrap). Inline, so it only affects this instance.
   */
  maxWidth?: string | number
  /**
   * When true the tooltip box itself is hoverable and its text is selectable: the
   * close is delayed by a grace period so the cursor can bridge the trigger->box
   * gap, and hovering the box cancels the close. Use for tooltips whose content the
   * user needs to read carefully or copy (e.g. a full commit hash / email). Leave
   * off for simple icon/nav labels, which stay snappy and click-through.
   */
  interactive?: boolean
  /**
   * Single focusable child (e.g. a <button>). It receives ref + hover/focus handlers +
   * aria-describedby so the trigger stays a real interactive, keyboard/touch element.
   */
  children: ReactElement<{
    ref?: React.Ref<HTMLElement>
    onMouseEnter?: (e: React.MouseEvent) => void
    onMouseLeave?: (e: React.MouseEvent) => void
    onFocus?: (e: React.FocusEvent) => void
    onBlur?: (e: React.FocusEvent) => void
    'aria-describedby'?: string
  }>
}

/**
 * Themed, placement-aware, scroll-aware tooltip rendered in a portal. Extracted from the
 * former duplicated NavTooltip / SettingsTooltip. The placement transform is applied
 * inline (the CSS only owns the opacity entrance animation + its reduced-motion guard),
 * so top/bottom placements are not forced to the right-placement translateY(-50%).
 */
export function Tooltip({ text, placement = 'top', delayMs = 30, maxWidth, interactive = false, children }: TooltipProps) {
  const [shown, setShown] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const anchorRef = useRef<HTMLElement | null>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Separate timer for the interactive close grace period so it can be cancelled
  // when the cursor reaches the box, independently of the open timer.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const id = useRef(`tip-${++tooltipCounter}`).current

  const transform = placement === 'right'
    ? 'translateY(-50%)'
    : placement === 'top'
      ? 'translate(-50%, -100%)'
      : 'translate(-50%, 0)'

  const updatePos = useCallback(() => {
    const el = anchorRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const tip = tipRef.current
    const tw = tip?.offsetWidth ?? 220
    const th = tip?.offsetHeight ?? 40
    let top: number, left: number
    if (placement === 'right') {
      left = r.right + 12
      // pos.top is the box CENTRE (translateY(-50%)), so clamp it half a box height off BOTH
      // viewport edges - a tall tooltip anchored near the top was clipping above the window.
      top = Math.min(Math.max(r.top + r.height / 2, th / 2 + 8), window.innerHeight - th / 2 - 8)
    } else {
      left = Math.min(Math.max(r.left + r.width / 2, tw / 2 + 8), window.innerWidth - tw / 2 - 8)
      top = placement === 'top' ? r.top - 8 : r.bottom + 8
    }
    setPos({ top, left })
  }, [placement])

  useEffect(() => {
    if (!shown) return
    updatePos()
    const el = anchorRef.current
    const ro = el ? new ResizeObserver(updatePos) : null
    if (el && ro) ro.observe(el)
    // Reposition while any ancestor scrolls (capture catches nested scroll containers).
    window.addEventListener('scroll', updatePos, { passive: true, capture: true })
    window.addEventListener('resize', updatePos, { passive: true })
    // Escape dismisses the tooltip (WCAG 1.4.13 "Content on Hover or Focus":
    // content must be dismissable). Cheap + correct for every tooltip, so applied
    // unconditionally rather than only when interactive.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (timer.current) clearTimeout(timer.current)
        if (closeTimer.current) clearTimeout(closeTimer.current)
        setShown(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      ro?.disconnect()
      window.removeEventListener('scroll', updatePos, { capture: true } as EventListenerOptions)
      window.removeEventListener('resize', updatePos)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [shown, updatePos])

  // Re-clamp after first paint once real tooltip size is known.
  useLayoutEffect(() => { if (shown && pos) updatePos() }, [shown]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }, [])

  const show = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    timer.current = setTimeout(() => setShown(true), delayMs)
  }
  // When interactive, delay the close so the cursor can bridge the trigger->box gap
  // and land on the (now hoverable) tooltip; otherwise close immediately as before.
  const hide = () => {
    if (timer.current) clearTimeout(timer.current)
    if (interactive) {
      if (closeTimer.current) clearTimeout(closeTimer.current)
      closeTimer.current = setTimeout(() => setShown(false), 120)
    } else {
      setShown(false)
    }
  }
  // Cursor reached the tooltip box: cancel the pending close so it stays open.
  const cancelHide = () => { if (closeTimer.current) clearTimeout(closeTimer.current) }

  if (!isValidElement(children)) {
    throw new Error('Tooltip expects a single React element child')
  }

  // Compose with any handlers + ref the child already has rather than clobbering them.
  const childProps = children.props
  const childRef = (children as ReactElement & { ref?: Ref<HTMLElement> }).ref
  const setRef = (node: HTMLElement | null) => {
    anchorRef.current = node
    if (typeof childRef === 'function') childRef(node)
    else if (childRef && typeof childRef === 'object') (childRef as React.MutableRefObject<HTMLElement | null>).current = node
  }
  const trigger = cloneElement(children, {
    ref: setRef,
    'aria-describedby': shown ? id : undefined,
    onMouseEnter: (e: React.MouseEvent) => { childProps.onMouseEnter?.(e); show() },
    onMouseLeave: (e: React.MouseEvent) => { childProps.onMouseLeave?.(e); hide() },
    onFocus: (e: React.FocusEvent) => { childProps.onFocus?.(e); show() },
    onBlur: (e: React.FocusEvent) => { childProps.onBlur?.(e); hide() },
  })

  // Portal into the trigger's containing <dialog> when there is one: a native dialog opened
  // with showModal() sits in the browser TOP LAYER, which paints above everything portaled to
  // document.body - a body-portaled tooltip is invisible inside any modal. position:fixed stays
  // viewport-relative inside the dialog (Modal.module.css deliberately uses contain:layout,
  // not paint, to keep fixed popovers un-clipped). anchorRef is always set once `shown` is true.
  const portalTarget = anchorRef.current?.closest('dialog') ?? document.body

  return (
    <>
      {trigger}
      {shown && pos && createPortal(
        <div
          ref={tipRef}
          id={id}
          role="tooltip"
          className={`tm-tooltip tm-tooltip--${placement}${interactive ? ' tm-tooltip--interactive' : ''}`}
          style={{ position: 'fixed', top: pos.top, left: pos.left, transform, ...(maxWidth !== undefined ? { maxWidth } : {}) }}
          {...(interactive ? { onMouseEnter: cancelHide, onMouseLeave: hide } : {})}
        >
          {text}
        </div>,
        portalTarget,
      )}
    </>
  )
}

/**
 * Anchor wrapper for placing a Tooltip on a NavLink (or any element that renders its own
 * focusable node) WITHOUT cloning props onto that node. Tooltip's cloneElement targets THIS
 * wrapper: ref + hover/focus handlers land on the wrapping <div> (the positioning anchor),
 * and `aria-describedby` is forwarded down via the render prop so it reaches the real
 * focusable element (e.g. NavLink's underlying <a>). The child stays pristine, so a
 * NavLink's isActive-driven style() still applies.
 */
export const TooltipAnchor = forwardRef<
  HTMLDivElement,
  {
    'aria-describedby'?: string
    onMouseEnter?: (e: React.MouseEvent) => void
    onMouseLeave?: (e: React.MouseEvent) => void
    onFocus?: (e: React.FocusEvent) => void
    onBlur?: (e: React.FocusEvent) => void
    /** Render the focusable child, forwarding the injected describedby onto it. */
    children: (describedBy: string | undefined) => ReactNode
  }
>(function TooltipAnchor(
  { 'aria-describedby': describedBy, onMouseEnter, onMouseLeave, onFocus, onBlur, children },
  ref,
) {
  return (
    <div ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onFocus={onFocus} onBlur={onBlur}>
      {children(describedBy)}
    </div>
  )
})
